import { useCallback, useState, useRef, useEffect } from 'react';
import { isValidSalesforceId } from 'src/helpers';
import api from '../api';

interface UseCheckCompanySalesforceIdResult {
  isLoading: boolean;
  error: string | null;
  isAvailable: boolean | null;
  checkSalesforceAccountId: (salesforceAccountId: string) => Promise<void>;
}

export default function useCheckCompanySalesforceId(): UseCheckCompanySalesforceIdResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on unmount - cancel any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const checkSalesforceAccountId = useCallback(async (salesforceAccountId: string) => {
    // Reset state if value is empty or invalid format
    if (!salesforceAccountId?.trim() || !isValidSalesforceId(salesforceAccountId)) {
      setIsLoading(false);
      setError(null);
      setIsAvailable(null);
      return;
    }

    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsLoading(true);
    setError(null);
    setIsAvailable(null);

    try {
      const result = await api.checkCompanySalesforceAccountIdAvailability(salesforceAccountId);

      // Only update state if this request wasn't cancelled
      if (!abortController.signal.aborted) {
        setIsAvailable(Boolean(result?.available));
      }
    } catch (err: unknown) {
      // Ignore abort errors (they're expected when cancelling)
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      // Only set error if request wasn't aborted
      if (!abortController.signal.aborted) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to check Salesforce Account ID availability';
        setError(errorMessage);
        setIsAvailable(null);
      }
    } finally {
      // Only update loading state if request wasn't aborted
      if (!abortController.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, []);

  return { isLoading, error, isAvailable, checkSalesforceAccountId };
}
